// Shared pieces of the merchant Gateway pages (Auto Pay + Payout).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, ExternalLink, RefreshCw } from "lucide-react";
import api, { API, formatApiError } from "../../../lib/api";
import {
  Card, CopyField, DataTable, Drawer, Empty, Field, Json, KV, Pager, StatusBadge, Timeline,
  ago, copyText, inr, shortId, usePagedList, usePolling, when,
} from "../../../components/sc/ui";

export const errMsg = (e) => formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong";

export function exportCsv(path, filters) {
  const p = new URLSearchParams();
  Object.entries(filters || {}).forEach(([k, v]) => { if (v && v !== "all") p.set(k, v); });
  window.open(`${API}${path}?${p.toString()}`, "_blank", "noopener");
}

export function DateRange({ filters, setFilter }) {
  return (
    <>
      <input type="date" className="ui-input" style={{ width: 150 }} value={filters.date_from || ""} onChange={(e) => setFilter("date_from", e.target.value)} aria-label="From date" />
      <input type="date" className="ui-input" style={{ width: 150 }} value={filters.date_to || ""} onChange={(e) => setFilter("date_to", e.target.value)} aria-label="To date" />
    </>
  );
}

// ─────────────────────────── callbacks sent to the merchant ───────────────────────────

const DELIVERY_STATUSES = ["all", "delivered", "retrying", "failed", "pending", "no_url"];

export function DeliveryList({ kind = "all", live = true, testId = "gw-deliveries" }) {
  const list = usePagedList((params) => api.get("/gateway/deliveries", { params: { ...params, kind } }).then((r) => r.data), { status: "all" }, 25);
  const [open, setOpen] = useState(null);
  usePolling(list.load, 4000, live && list.page === 1);

  const cols = [
    { key: "at", label: "Sent", render: (d) => <span className="ui-mono" style={{ fontSize: 12 }}>{when(d.created_at, true)}<span className="ui-cell-sub">{ago(d.created_at)}</span></span> },
    { key: "event", label: "Event", render: (d) => <span className="ui-mono ui-cell-main" style={{ fontSize: 12 }}>{d.event}</span> },
    { key: "order", label: "Order ID", render: (d) => <span className="ui-mono ui-trunc" style={{ fontSize: 12 }} title={d.order_id}>{d.order_id || shortId(d.txn_id)}</span> },
    { key: "resp", label: "Response", render: (d) => <span className="ui-mono" style={{ fontSize: 12 }}>{d.last_status_code || "—"} · {d.attempts} try</span> },
    { key: "status", label: "Status", render: (d) => <StatusBadge status={d.status} /> },
  ];
  return (
    <Card pad={false} title="Callbacks sent to your server" sub="Every signed event SuperCash POSTed to your webhook URL, with retries."
      actions={
        <select className="ui-select" value={list.filters.status} onChange={(e) => list.setFilter("status", e.target.value)}>
          {DELIVERY_STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s.replace("_", " ")}</option>)}
        </select>
      }>
      <DataTable testId={testId} columns={cols} rows={list.data.items} loading={list.loading} fresh={list.fresh}
        onRowClick={setOpen} activeId={open?.id}
        empty={<Empty icon="🔔" title="No callbacks yet">They appear here the moment a transaction completes.</Empty>} />
      {list.data.total > 0 && <Pager page={list.data.page} pages={list.data.pages} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />}
      {open && <DeliveryDrawer d={open} onClose={() => setOpen(null)} onChanged={(x) => { setOpen(x); list.load(); }} />}
    </Card>
  );
}

export function DeliveryDrawer({ d, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/gateway/deliveries/${d.id}/resend`);
      toast[data.status === "delivered" ? "success" : "error"](data.status === "delivered" ? "Delivered" : `Not delivered: ${data.last_error || data.status}`);
      onChanged(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer title={d.event} sub={<span className="ui-mono">{d.order_id || d.txn_id}</span>} onClose={onClose}
      actions={<button className="ui-btn ui-btn-sm" onClick={resend} disabled={busy}>{busy ? "Sending…" : "↻ Resend"}</button>}>
      <KV items={[
        ["Status", <StatusBadge status={d.status} />],
        ["URL", d.url, { copy: true }],
        ["Attempts", d.attempts],
        ["Last response", d.last_status_code],
        ["Next retry", d.next_attempt_at ? when(d.next_attempt_at, true) : null],
        ["Delivered", d.delivered_at ? when(d.delivered_at, true) : null],
      ]} />
      {d.last_error && <div style={{ color: "#b3261e", fontSize: 13 }}>Last error: {d.last_error}</div>}
      <div>
        <div className="ui-eyebrow" style={{ marginBottom: 6 }}>Payload (signed with X-SuperCash-Signature)</div>
        <Json value={d.payload} />
      </div>
      <div>
        <div className="ui-eyebrow" style={{ marginBottom: 6 }}>Attempts</div>
        {(d.history || []).length === 0 ? <div className="ui-faint" style={{ fontSize: 13 }}>Not attempted yet.</div> :
          d.history.slice().reverse().map((h, i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--ui-line-soft)", fontSize: 12.5 }}>
              <div className="ui-between">
                <span className="ui-mono">{when(h.at, true)}</span>
                <StatusBadge status={h.error ? "failed" : "delivered"} label={h.status_code ? `HTTP ${h.status_code}` : "no response"} />
              </div>
              {h.latency_ms != null && <span className="ui-faint ui-mono" style={{ fontSize: 11 }}>{h.latency_ms} ms</span>}
              {h.error && <div style={{ color: "#b3261e" }}>{h.error}</div>}
              {h.response && <pre className="ui-json" style={{ marginTop: 6, maxHeight: 120 }}>{h.response}</pre>}
            </div>
          ))}
      </div>
    </Drawer>
  );
}

// ─────────────────────────── transaction drawer ───────────────────────────

function CopyIcon({ value }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button type="button" className="txd-copy" aria-label="Copy" title="Copy"
      onClick={async () => { await copyText(String(value)); setDone(true); setTimeout(() => setDone(false), 1200); }}>
      {done ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

const cap = (v) => (v ? String(v).charAt(0).toUpperCase() + String(v).slice(1).replace(/_/g, " ") : v);

// Labelled rows; empty values are hidden so each section only shows what's known.
function DetailSection({ title, rows }) {
  const shown = rows.filter(([, v]) => v != null && v !== "");
  if (!shown.length) return null;
  return (
    <section className="txd-section">
      <h4>{title}</h4>
      {shown.map(([k, v, o = {}]) => (
        <div key={k} className="txd-row">
          <span className="txd-k">{k}</span>
          <span className={`txd-v${o.mono ? " is-mono" : ""}`}>{v}{o.copy && <CopyIcon value={v} />}</span>
        </div>
      ))}
    </section>
  );
}

export function TxnDrawer({ kind, id, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const base = kind === "payin" ? "/gateway/payins" : "/gateway/payouts";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`${base}/${id}`);
      setT(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [base, id]);
  useEffect(() => { load(); }, [load]);
  usePolling(load, 4000, true);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (ok) toast.success(ok);
      if (r?.data?.events) setT(r.data); else await load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!t) return <Drawer title="Loading…" onClose={onClose}><div className="ui-skel" style={{ height: 120 }} /></Drawer>;
  const open = !["success", "failed", "dropped", "error"].includes(t.status);
  const payin = kind === "payin";
  const sections = payin ? [
    ["Payment", [
      ["UTR", t.utr, { copy: true, mono: true }],
      ["Payer UPI ID", t.payer_vpa, { mono: true }],
      ["Amount received", t.amount_received != null && t.amount_received !== t.amount ? inr(t.amount_received) : null],
      // Older records only: SuperCash no longer charges a fee.
      ["Fee", t.fee ? inr(t.fee) : null],
      ["Net credited", t.fee && t.net_amount ? inr(t.net_amount) : null],
    ]],
    ["Customer", [
      ["Name", t.customer?.name],
      ["Phone", t.customer?.phone, { mono: true }],
      ["Email", t.customer?.email],
      ["Customer ID", !t.customer?.name && !t.customer?.phone ? t.customer?.id : null, { mono: true }],
    ]],
    ["Reference", [
      ["Order ID", t.order_id, { copy: true, mono: true }],
      ["Attempt", t.attempt_ref, { copy: true, mono: true }],
      ["Checkout", t.checkout_url ? <a className="txd-link" href={t.checkout_url} target="_blank" rel="noreferrer">Open page <ExternalLink size={13} /></a> : null],
      ["Source", cap(t.source)],
    ]],
    ["Dates", [["Created", when(t.created_at, true)], ["Completed", t.completed_at ? when(t.completed_at, true) : null]]],
  ] : [
    ["Beneficiary", [
      ["Name", t.beneficiary?.name],
      ["Account number", t.beneficiary?.account_number, { copy: true, mono: true }],
      ["IFSC", t.beneficiary?.ifsc, { mono: true }],
    ]],
    ["Payment", [
      ["UTR", t.utr, { copy: true, mono: true }],
      ["Failure reason", cap(t.failure_reason)],
      ["Refunded", t.refunded ? "Yes — returned to balance" : null],
      ["Fee", t.fee ? inr(t.fee) : null],
      ["Total debited", t.fee ? inr(t.total_debit) : null],
    ]],
    ["Reference", [
      ["Payout ID", t.id, { copy: true, mono: true }],
      ["Order ID", t.order_id, { copy: true, mono: true }],
      ["Source", cap(t.source)],
    ]],
    ["Dates", [["Created", when(t.created_at, true)], ["Completed", t.completed_at ? when(t.completed_at, true) : null]]],
  ];
  return (
    <Drawer onClose={onClose} title={payin ? "Payin details" : "Payout details"}
      actions={open && <button className="ui-btn ui-btn-sm" disabled={busy} onClick={() => act(() => api.post(`${base}/${id}/refresh`), "Status refreshed")}><RefreshCw size={14} /> Refresh</button>}>
      <div className="txd-hero">
        <div className="txd-kind">{payin ? "Payin · Auto Pay" : `Payout · to ${t.beneficiary?.name || "bank account"}`}</div>
        <div className="txd-amount">{inr(t.amount)}</div>
        <div className="txd-meta">
          <StatusBadge status={t.status} />
          <span>{ago(t.created_at)}</span>
        </div>
        <div className="txd-ref"><span>{t.order_id}</span><CopyIcon value={t.order_id} /></div>
      </div>

      {t.error && <div className="ui-banner is-danger" style={{ fontSize: 13 }}>{t.error.message} <span className="ui-mono">({t.error.code})</span></div>}

      {sections.map(([title, rows]) => <DetailSection key={title} title={title} rows={rows} />)}

      <section className="txd-section">
        <h4>Timeline</h4>
        <div style={{ padding: "14px 16px 2px" }}><Timeline events={t.events} /></div>
      </section>

      <section className="txd-section">
        <h4>Callbacks to your server <span className="txd-count">{t.deliveries.length}</span></h4>
        {t.deliveries.length === 0 ? (
          <div className="txd-empty">{payin ? "P2G payments notify you through your normal webhook (payment.success / order.paid) — see Callbacks." : "Sent automatically when this payout completes."}</div>
        ) : t.deliveries.map((d) => (
          <button key={d.id} onClick={() => setDelivery(d)} className="txd-row txd-row-btn">
            <span className="txd-k"><span className="ui-mono" style={{ color: "var(--ui-text)" }}>{d.event}</span> · {when(d.created_at, true)}</span>
            <StatusBadge status={d.status} />
          </button>
        ))}
      </section>
      {delivery && <DeliveryDrawer d={delivery} onClose={() => setDelivery(null)} onChanged={(x) => { setDelivery(x); load(); }} />}
    </Drawer>
  );
}

// ─────────────────────────── compact transaction list ───────────────────────────

const TX_TONE = {
  success: ["#0f7a45", "#E3F7EC"], pending: ["#8a6a00", "#FFF6D8"], initiated: ["#4931E8", "#ECE8FB"],
  creating: ["#4931E8", "#ECE8FB"], review: ["#b26a00", "#FFF0D9"], dispute: ["#a14a00", "#FFE9D6"],
  failed: ["#b3261e", "#FBE9E7"], error: ["#b3261e", "#FBE9E7"], dropped: ["#6a6478", "#F1ECE0"],
};
const TX_LABEL = { initiated: "awaiting", creating: "creating" };

export function TxPill({ status }) {
  const [fg, bg] = TX_TONE[status] || ["#6a6478", "#F1ECE0"];
  return <span className="tx-pill" style={{ color: fg, background: bg }}>{TX_LABEL[status] || status}</span>;
}

/** Search + status chips (with counts) + date range + CSV, for a usePagedList. */
export function TxToolbar({ list, qInput, setQInput, statuses, counts = {}, placeholder, onExport }) {
  return (
    <>
      <div className="tx-bar">
        <label className="tx-search">
          <input className="ui-input" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder={placeholder} aria-label="Search" />
        </label>
        <span className="tx-dates">
          <input type="date" className="ui-input" value={list.filters.date_from || ""} onChange={(e) => list.setFilter("date_from", e.target.value)} aria-label="From date" />
          <input type="date" className="ui-input" value={list.filters.date_to || ""} onChange={(e) => list.setFilter("date_to", e.target.value)} aria-label="To date" />
        </span>
        <button className="ui-btn ui-btn-sm" onClick={onExport} title="Download CSV of the current filter">↓ CSV</button>
      </div>
      <div className="tx-chips" role="tablist" aria-label="Filter by status">
        {statuses.map((s) => {
          const on = (list.filters.status || "all") === s;
          const n = s === "all" ? Object.values(counts).reduce((a, c) => a + (c?.count || 0), 0) : counts[s]?.count || 0;
          return (
            <button key={s} role="tab" aria-selected={on} className={`tx-chip ${on ? "is-on" : ""}`} onClick={() => list.setFilter("status", s)}>
              {s !== "all" && <i style={{ background: (TX_TONE[s] || ["#6a6478"])[0] }} />}
              {s === "all" ? "All" : TX_LABEL[s] || s}
              <span style={{ opacity: 0.6, fontFamily: "JetBrains Mono, monospace", fontSize: 11 }}>{n.toLocaleString("en-IN")}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

/**
 * rows: [{ id, status, title, sub, who, whoSub, ref, at, amount, amountSub }]
 * Desktop: one dense line per txn. Phones: two-line card (id + amount / who·time + status).
 */
export function TxList({ rows, loading, fresh, activeId, onOpen, headers, empty, footer }) {
  if (loading && !rows.length) {
    return <div style={{ padding: 18, display: "grid", gap: 12 }}>{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="ui-skel" style={{ height: 16, width: `${92 - i * 7}%` }} />)}</div>;
  }
  return (
    <>
      {rows.length === 0 ? empty : (
        <div className="tx-list" role="table">
          <div className="tx-head" role="row">
            <span />
            {headers.map((h, i) => <span key={h} className={i === 2 ? "tx-col-ref" : ""} style={i >= 4 ? { textAlign: "right" } : undefined}>{h}</span>)}
          </div>
          {rows.map((r) => {
            const [fg, bg] = TX_TONE[r.status] || ["#6a6478", "#F1ECE0"];
            return (
              <div key={r.id} role="row" tabIndex={0} data-testid={`tx-row-${r.id}`}
                className={`tx-row ${activeId === r.id ? "is-active" : ""} ${fresh?.has(r.id) ? "is-new" : ""}`}
                onClick={() => onOpen(r.id)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(r.id); } }}>
                <span className="tx-dot" style={{ background: fg, "--tx-ring": bg }} />
                <span className="tx-main"><span className="tx-id" title={r.title}>{r.title}</span><span className="tx-sub">{r.sub}</span></span>
                <span className="tx-main"><span className="tx-who" title={r.who}>{r.who || "—"}</span>{r.whoSub && <span className="tx-sub">{r.whoSub}</span>}</span>
                <span className="tx-main tx-col-ref"><span className="tx-ref" title={r.ref}>{r.ref || "—"}</span>{r.refSub && <span className="tx-sub">{r.refSub}</span>}</span>
                <span className="tx-time" title={when(r.at, true)}>{ago(r.at)}</span>
                <span className="tx-amt">{inr(r.amount)}{r.amountSub && <small>{r.amountSub}</small>}</span>
                <TxPill status={r.status} />
                <span className="tx-mobile">
                  <span className="tx-id">{r.title}</span>
                  <span className="tx-amt">{inr(r.amount)}</span>
                  <span className="tx-sub">{[r.who, ago(r.at)].filter(Boolean).join(" · ")}</span>
                  <span style={{ justifySelf: "end" }}><TxPill status={r.status} /></span>
                </span>
              </div>
            );
          })}
        </div>
      )}
      {footer}
    </>
  );
}
